import { getProgressLevel } from 'pages/Employees/Availability/availability-helper';
import React from 'react';
import styled, { useTheme } from 'styled-components';

/**
 * Belastningen de senaste 28 dygnen för en kandidat — samma tal och samma trösklar som
 * tillgänglighetsvyns stapel (`Körtid senaste 28d`).
 *
 * Varför den finns: rangordningen är närhet, och trafikledaren såg därför aldrig att en
 * föreslagen fälttestare låg bland de tyngst lastade i länet — den uppgiften fanns bara i en
 * annan vy. Pillret självt ändrar ingenting; det gör valet möjligt att göra för hand. Belastningen
 * avgör i planeringen bara EXAKT lika poäng, via tie-breaken i `CandidateScore`
 * (TL-feedback punkt 10, `ruttplanering-tl-feedback-2026-09-15.md`).
 *
 * Trösklarna importeras från tillgänglighetsvyns hjälpare i stället för att skrivas om här —
 * annars kan orange börja betyda olika saker i de två vyerna.
 */
const Pill = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-size: 0.8em;
  color: ${({ color }) => color};
  white-space: nowrap;
`;

const Window = styled.span`
  color: rgb(120, 120, 120);
`;

type Props = {
  hoursLast28Days?: number | null;
  maxHoursPer4Weeks?: number | null;
  loadPercent?: number | null;
  availabilityWindow?: string | null;
};

const LoadPill: React.FC<Props> = ({
  hoursLast28Days,
  maxHoursPer4Weeks,
  loadPercent,
  availabilityWindow,
}) => {
  const theme = useTheme();

  // Utan körtidsrapport finns inget tal att visa. En nolla hade varit ett påstående om att
  // personen inte kört alls, vilket är något helt annat än att vi inte vet.
  if (hoursLast28Days === null || hoursLast28Days === undefined) {
    return availabilityWindow ? <Window>{availabilityWindow}</Window> : null;
  }

  // Utan tak finns ingen kvot att färga. Att falla tillbaka på 0 hade gett GRÖNT — en falsk
  // trygghetssignal i exakt det ögonblick trafikledaren väljer ersättare, och samma misstag som
  // backend medvetet undviker genom att lämna LoadPercent null i stället för 0.
  const hasQuota = loadPercent !== null && loadPercent !== undefined;
  const level = hasQuota ? getProgressLevel(loadPercent) : null;
  const color = !hasQuota
    ? theme.colors.foreground.tertiary
    : level === 'over'
      ? theme.colors.foreground.error
      : level === 'warning'
        ? theme.colors.foreground.warning
        : theme.colors.foreground.good;

  const quota = hasQuota
    ? ` av ${Math.round(maxHoursPer4Weeks ?? 0)} h (${Math.round(loadPercent)} %)`
    : '';

  return (
    <Pill
      color={color}
      title={`Körtid senaste 28 dygnen${quota ? `${quota} av taket` : ' (inget tak satt — ingen kvot att jämföra mot)'}. Samma tal som tillgänglighetsvyn visar. Avgör bara valet när två kandidater ligger exakt lika nära.`}
    >
      28d {Math.round(hoursLast28Days)} h{quota}
      {availabilityWindow && <Window>{availabilityWindow}</Window>}
    </Pill>
  );
};

export default LoadPill;
